import React from "react";
import { Card, List, Button, Tag, Space, Typography, Tooltip } from "antd";
import { QuestionCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { TariffFormValues } from "../utils/types";

const { Text } = Typography;

interface TariffSuggestionsProps {
  suggestions: Array<{
    suggestion: Partial<TariffFormValues>;
    explanation: string;
  }>;
  onSelectSuggestion: (suggestion: Partial<TariffFormValues>) => void;
  transportTypes: { id: number; name: string }[];
  originPorts: { id: number; name: string; country: string }[];
  destinationPorts: { id: number; name: string; country: string }[];
  containerSizes: { id: number; name: string }[];
  bookingTypes: { id: number; name: string }[];
}

const TariffSuggestions: React.FC<TariffSuggestionsProps> = ({
  suggestions,
  onSelectSuggestion,
  transportTypes,
  originPorts,
  destinationPorts,
  containerSizes,
  bookingTypes,
}) => {
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  // Helper functions to get display names
  const getTransportTypeName = (id: number | null) => {
    if (id === null) return "Any";
    const type = transportTypes.find((t) => t.id === id);
    return type ? type.name : "Unknown";
  };

  const getPortName = (id: number | null, ports: typeof originPorts) => {
    if (id === null) return "Any";
    const port = ports.find((p) => p.id === id);
    return port ? `${port.name} (${port.country})` : "Unknown";
  };

  const getContainerSizeName = (id: number | null) => {
    if (id === null) return "Any";
    const size = containerSizes.find((s) => s.id === id);
    return size ? size.name : "Unknown";
  };

  const getBookingTypeName = (id: number | null) => {
    if (id === null) return "Any";
    const type = bookingTypes.find((t) => t.id === id);
    return type ? type.name : "Unknown";
  };

  return (
    <Card
      title={
        <Space>
          <span>Suggested Tariffs</span>
          <Tooltip title="These suggestions are based on your existing tariffs and current selections">
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      }
      size="small"
      style={{ marginBottom: 16 }}
    >
      <List
        dataSource={suggestions}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Button
                key="use"
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => onSelectSuggestion(item.suggestion)}
              >
                Use
              </Button>,
            ]}
          >
            <List.Item.Meta
              title={item.explanation}
              description={
                <Space direction="vertical" size={1}>
                  <Space wrap>
                    {item.suggestion.transport_type && (
                      <Tag color="blue">
                        {getTransportTypeName(item.suggestion.transport_type)}
                      </Tag>
                    )}
                    {item.suggestion.origin_port && (
                      <Tag color="green">
                        Origin:{" "}
                        {getPortName(item.suggestion.origin_port, originPorts)}
                      </Tag>
                    )}
                    {item.suggestion.destination_port && (
                      <Tag color="green">
                        Dest:{" "}
                        {getPortName(
                          item.suggestion.destination_port,
                          destinationPorts
                        )}
                      </Tag>
                    )}
                    {item.suggestion.container_size && (
                      <Tag color="orange">
                        {getContainerSizeName(item.suggestion.container_size)}
                      </Tag>
                    )}
                    {item.suggestion.bookings_type && (
                      <Tag color="purple">
                        {getBookingTypeName(item.suggestion.bookings_type)}
                      </Tag>
                    )}
                    {item.suggestion.price && (
                      <Tag color="red">€{item.suggestion.price.toFixed(2)}</Tag>
                    )}
                  </Space>
                  <Text type="secondary">
                    {item.suggestion.from
                      ? dayjs(item.suggestion.from).format("YYYY-MM-DD")
                      : "No start date"}{" "}
                    -{" "}
                    {item.suggestion.to
                      ? dayjs(item.suggestion.to).format("YYYY-MM-DD")
                      : "No end date"}
                  </Text>
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );
};

export default TariffSuggestions;
